import { requiredMedia, type PlayerManifest } from '@signage/shared';
import type {
  CacheEntry,
  ClockAdapter,
  DatabaseAdapter,
  FileSystemAdapter,
  LoggerAdapter,
} from './adapters.js';

export interface CacheStats {
  cachedBytes: number;
  fileCount: number;
  freeBytes: number;
  totalBytes: number;
}

export interface GarbageCollectionResult {
  removed: string[];
  freedBytes: number;
  keptInUse: number;
}

/**
 * Local media cache.
 *
 * Files are stored under their sha256, so identical content is shared between playlists and a
 * changed file is a different file. Garbage collection removes only entries that no manifest
 * we still care about references — a file used by the active manifest, or by a pending
 * manifest we are still downloading, is never touched.
 */
export class CacheManager {
  constructor(
    private readonly fs: FileSystemAdapter,
    private readonly db: DatabaseAdapter,
    private readonly clock: ClockAdapter,
    private readonly logger: LoggerAdapter,
    private readonly cacheDir: string,
  ) {}

  pathFor(sha256: string): string {
    return this.fs.join(this.cacheDir, sha256);
  }

  async stats(): Promise<CacheStats> {
    const entries = this.db.listCacheEntries();
    const cachedBytes = entries.reduce((sum, e) => sum + e.sizeBytes, 0);
    let disk = { freeBytes: 0, totalBytes: 0 };
    try {
      disk = await this.fs.diskSpace(this.cacheDir);
    } catch {
      /* some platforms cannot report disk space; report zeros rather than failing */
    }
    return {
      cachedBytes,
      fileCount: entries.length,
      freeBytes: disk.freeBytes,
      totalBytes: disk.totalBytes,
    };
  }

  record(entry: Omit<CacheEntry, 'downloadedAt' | 'lastUsedAt'> & { lastUsedAt?: number }): void {
    const now = this.clock.now();
    this.db.upsertCacheEntry({
      ...entry,
      downloadedAt: now,
      lastUsedAt: entry.lastUsedAt ?? now,
    });
  }

  markUsed(sha256: string): void {
    this.db.touchCacheEntry(sha256, this.clock.now());
  }

  /**
   * Which of the manifest's media files are missing or unusable locally?
   * A file counts as present only when the cache row, the file on disk, and the expected size
   * all agree — anything else is re-downloaded.
   */
  async missingFor(manifest: PlayerManifest): Promise<{
    missing: ReturnType<typeof requiredMedia>;
    present: ReturnType<typeof requiredMedia>;
  }> {
    const wanted = requiredMedia(manifest);
    const missing: typeof wanted = [];
    const present: typeof wanted = [];

    for (const media of wanted) {
      const path = this.pathFor(media.sha256);
      const stat = await this.fs.stat(path);
      if (stat && stat.size === media.sizeBytes) {
        present.push(media);
        continue;
      }
      if (stat) {
        this.logger.warn('Cached file size does not match the manifest', {
          mediaId: media.mediaId,
          expected: media.sizeBytes,
          actual: stat.size,
        });
      }
      missing.push(media);
    }
    return { missing, present };
  }

  /**
   * Verifies cached files against their checksums. Full verification is expensive, so the
   * player runs it on boot for a bounded number of files and whenever corruption is suspected.
   */
  async verify(
    hashes: string[],
    options: { limit?: number } = {},
  ): Promise<{ verified: string[]; corrupt: string[] }> {
    const verified: string[] = [];
    const corrupt: string[] = [];
    const limit = options.limit ?? hashes.length;

    for (const sha256 of hashes.slice(0, limit)) {
      const path = this.pathFor(sha256);
      const stat = await this.fs.stat(path);
      if (!stat) {
        corrupt.push(sha256);
        continue;
      }
      try {
        const actual = await this.fs.hashFile(path);
        if (actual === sha256) {
          verified.push(sha256);
          const entry = this.db.getCacheEntry(sha256);
          if (entry && !entry.verified) {
            this.db.upsertCacheEntry({ ...entry, verified: true });
          }
        } else {
          corrupt.push(sha256);
        }
      } catch {
        corrupt.push(sha256);
      }
    }

    for (const sha256 of corrupt) {
      this.logger.error('Removing a corrupt cached file', { sha256 });
      await this.fs.remove(this.pathFor(sha256)).catch(() => {});
      this.db.deleteCacheEntry(sha256);
    }
    return { verified, corrupt };
  }

  /**
   * Removes cached files that nothing references, oldest first, and enforces the cache size
   * limit. Files required by `protectedHashes` (active manifest, pending manifest, currently
   * playing item) are never removed, even when that means exceeding the limit — a full cache
   * is better than a blank screen.
   */
  async collectGarbage(
    protectedHashes: Set<string>,
    options: { maxCacheBytes?: number; graceMs?: number } = {},
  ): Promise<GarbageCollectionResult> {
    const graceMs = options.graceMs ?? 5 * 60_000;
    const now = this.clock.now();
    const entries = this.db.listCacheEntries();
    const removed: string[] = [];
    let freedBytes = 0;
    let keptInUse = 0;

    const unreferenced = entries
      .filter((e) => {
        if (protectedHashes.has(e.sha256)) {
          keptInUse += 1;
          return false;
        }
        // Give a just-downloaded file a moment before considering it garbage, so a file
        // fetched for a manifest that is still activating is not deleted from under us.
        return now - e.downloadedAt > graceMs;
      })
      .sort((a, b) => a.lastUsedAt - b.lastUsedAt);

    for (const entry of unreferenced) {
      await this.fs.remove(this.pathFor(entry.sha256)).catch(() => {});
      this.db.deleteCacheEntry(entry.sha256);
      removed.push(entry.sha256);
      freedBytes += entry.sizeBytes;
    }

    // Enforce the size limit using only files that are still not protected.
    if (options.maxCacheBytes) {
      let total = this.db
        .listCacheEntries()
        .reduce((sum, e) => sum + e.sizeBytes, 0);
      if (total > options.maxCacheBytes) {
        const evictable = this.db
          .listCacheEntries()
          .filter((e) => !protectedHashes.has(e.sha256))
          .sort((a, b) => a.lastUsedAt - b.lastUsedAt);
        for (const entry of evictable) {
          if (total <= options.maxCacheBytes) break;
          await this.fs.remove(this.pathFor(entry.sha256)).catch(() => {});
          this.db.deleteCacheEntry(entry.sha256);
          removed.push(entry.sha256);
          freedBytes += entry.sizeBytes;
          total -= entry.sizeBytes;
        }
        if (total > options.maxCacheBytes) {
          this.logger.warn(
            'The cache exceeds its limit but the remaining files are all in use',
            { totalBytes: total, limitBytes: options.maxCacheBytes },
          );
        }
      }
    }

    if (removed.length > 0) {
      this.logger.info('Cache garbage collected', { removed: removed.length, freedBytes });
    }
    return { removed, freedBytes, keptInUse };
  }

  /** Hashes referenced by a manifest; used to build the protected set. */
  hashesFor(manifest: PlayerManifest | null): Set<string> {
    if (!manifest) return new Set();
    return new Set(requiredMedia(manifest).map((m) => m.sha256));
  }

  /** Rebuilds cache rows from what is actually on disk (recovery after a crash). */
  async reconcile(manifest: PlayerManifest | null): Promise<{ added: number; dropped: number }> {
    let names: string[] = [];
    try {
      names = await this.fs.listDir(this.cacheDir);
    } catch {
      return { added: 0, dropped: 0 };
    }
    const onDisk = new Set(names.filter((n) => /^[0-9a-f]{64}$/.test(n)));
    const rows = this.db.listCacheEntries();
    let dropped = 0;
    let added = 0;

    for (const row of rows) {
      if (!onDisk.has(row.sha256)) {
        this.db.deleteCacheEntry(row.sha256);
        dropped += 1;
      }
    }

    const known = new Set(rows.map((r) => r.sha256));
    const mediaByHash = new Map(
      (manifest ? requiredMedia(manifest) : []).map((m) => [m.sha256, m]),
    );
    for (const sha256 of onDisk) {
      if (known.has(sha256)) continue;
      const stat = await this.fs.stat(this.pathFor(sha256));
      if (!stat) continue;
      const media = mediaByHash.get(sha256);
      this.record({
        sha256,
        mediaId: media?.mediaId ?? sha256,
        path: this.pathFor(sha256),
        sizeBytes: stat.size,
        mimeType: media?.mimeType ?? 'application/octet-stream',
        verified: false,
      });
      added += 1;
    }

    if (added > 0 || dropped > 0) {
      this.logger.info('Cache reconciled with disk', { added, dropped });
    }
    return { added, dropped };
  }
}
